// XLSparkArea - Sprint 44 Fase 3 Vista 1.
// Grafico SVG area + linea. Mismo algoritmo visual que el legacy
// (LineChart en DashboardPage.jsx) pero tokenizado con CSS vars + dark
// mode + aria + reduced-motion safe. Sin libs externas.
//
// Props:
//   serie: Array<{fecha: 'YYYY-MM-DD', total_cop: number}>
//   height?: number (default 200)
//   ariaLabel?: string
//   emptyMessage?: string
//   formatValue?: (n) => string   para el tooltip del punto
//   formatCompact?: (n) => string para eje Y
//   formatX?: (iso) => string     para eje X
import { useEffect, useMemo } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import { niceScale } from '../utils';

const CSS_ID = 'xl-sparkarea-css';
const GRAD_ID = 'xl-sparkarea-grad';
const CSS = `
.xl-spark {
  width: 100%;
  display: block;
  color: var(--xl-color-primary);
}
.xl-spark__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: var(--xl-color-text-muted);
  font-family: var(--xl-font-sans);
  font-size: 12px;
  border: 1px dashed var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  background: var(--xl-color-surface-alt);
}
.xl-spark__grid   { stroke: var(--xl-color-border); stroke-opacity: 0.6 }
.xl-spark__axis   { fill: var(--xl-color-text-muted); font: 500 10px var(--xl-font-sans) }
.xl-spark__line   { stroke: var(--xl-color-primary); }
.xl-spark__area   { fill: url(#${GRAD_ID}) }
.xl-spark__dot    { fill: var(--xl-color-primary) }
.xl-spark__dot-hi { fill: var(--xl-color-primary); fill-opacity: 0.18 }
`;

export default function XLSparkArea({
  serie = [],
  height = 200,
  ariaLabel = 'Serie temporal',
  emptyMessage = 'Sin datos',
  formatValue,
  formatCompact,
  formatX,
}) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const width = 900;
  const padL = 52, padR = 16, padT = 8, padB = 26;

  const chart = useMemo(() => {
    if (!serie || serie.length === 0) return null;
    const values = serie.map((d) => Number(d.total_cop || 0));
    const dataMax = values.length ? Math.max.apply(null, values) : 0;
    const scale = niceScale(dataMax, 6);
    const range = scale.max || 1;
    const innerW = width - padL - padR;
    const innerH = height - padT - padB;
    const n = serie.length;
    const stepX = n > 1 ? innerW / (n - 1) : 0;

    const pts = serie.map((d, i) => {
      const v = Number(d.total_cop || 0);
      const x = padL + i * stepX;
      const y = padT + (innerH - (v / range) * innerH);
      return { x, y, v, fecha: d.fecha };
    });

    const linePath = pts
      .map((p, i) => (i === 0 ? 'M' : 'L') + p.x.toFixed(1) + ',' + p.y.toFixed(1))
      .join(' ');

    const baseY = padT + innerH;
    const areaPath =
      'M' + pts[0].x.toFixed(1) + ',' + baseY.toFixed(1) + ' ' +
      pts.map((p) => 'L' + p.x.toFixed(1) + ',' + p.y.toFixed(1)).join(' ') +
      ' L' + pts[pts.length - 1].x.toFixed(1) + ',' + baseY.toFixed(1) + ' Z';

    const yRows = scale.ticks.map((v) => ({
      v,
      y: padT + (innerH - (v / range) * innerH),
    }));

    const labelEvery = Math.max(1, Math.round(n / 6));
    const xRows = pts
      .map((p, i) => ({ ...p, show: i % labelEvery === 0 || i === n - 1 }))
      .filter((p) => p.show);

    const last = pts[pts.length - 1];
    return { linePath, areaPath, yRows, xRows, pts, last };
  }, [serie, height]);

  const fmtY = typeof formatCompact === 'function'
    ? formatCompact
    : (v) => String(v);
  const fmtTitle = typeof formatValue === 'function'
    ? formatValue
    : (v) => String(v);
  const fmtXAxis = typeof formatX === 'function'
    ? formatX
    : (iso) => String(iso || '');

  if (!chart) {
    return <div className="xl-spark__empty" role="status">{emptyMessage}</div>;
  }

  return (
    <svg
      className="xl-spark"
      role="img"
      aria-label={ariaLabel}
      viewBox={'0 0 ' + width + ' ' + height}
      preserveAspectRatio="none"
      style={{ height: height + 'px' }}
    >
      <defs>
        <linearGradient id={GRAD_ID} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>

      {chart.yRows.map((t, i) => (
        <g key={'y-' + i}>
          <line
            className="xl-spark__grid"
            x1={padL} y1={t.y} x2={width - padR} y2={t.y}
            strokeWidth="1" vectorEffect="non-scaling-stroke"
          />
          <text
            className="xl-spark__axis"
            x={padL - 8} y={t.y + 3}
            textAnchor="end"
          >
            {fmtY(t.v)}
          </text>
        </g>
      ))}

      <path className="xl-spark__area" d={chart.areaPath} />
      <path
        className="xl-spark__line"
        d={chart.linePath}
        fill="none"
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />

      <circle
        className="xl-spark__dot-hi"
        cx={chart.last.x} cy={chart.last.y} r="6"
        vectorEffect="non-scaling-stroke"
      />
      <circle
        className="xl-spark__dot"
        cx={chart.last.x} cy={chart.last.y} r="3"
        vectorEffect="non-scaling-stroke"
      />

      {chart.xRows.map((p, i) => (
        <text
          key={'x-' + i}
          className="xl-spark__axis"
          x={p.x} y={height - padB + 16}
          textAnchor="middle"
        >
          {fmtXAxis(p.fecha)}
        </text>
      ))}

      {chart.pts.map((p, i) => (
        <circle key={'h-' + i} cx={p.x} cy={p.y} r="12" fill="transparent">
          <title>{fmtXAxis(p.fecha) + ' \u2014 ' + fmtTitle(p.v)}</title>
        </circle>
      ))}
    </svg>
  );
}
